import { Clock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { ONBOARDING_STATUS_LABELS, type OnboardingCase, type OnboardingStatus } from '../types/onboarding';

interface CandidateTimelineProps {
  candidate: OnboardingCase;
  liveState?: string;
}

const formatDateTime = (value: string | null): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const labelForState = (state: string): string =>
  ONBOARDING_STATUS_LABELS[state as OnboardingStatus] ?? state;

interface TimelineEntry {
  icon: React.ReactNode;
  title: string;
  detail: string;
  tone: 'neutral' | 'success' | 'error';
}

export const CandidateTimeline = ({ candidate, liveState }: CandidateTimelineProps) => {
  const state = liveState || candidate.state;

  // Minimal timeline assembled from the case fields the API exposes today.
  // The list/detail endpoint does not yet return a per-transition history;
  // when that arrives, render it here instead of the static milestones below.
  const entries: TimelineEntry[] = [];
  entries.push({
    icon: <Clock className="h-4 w-4" />,
    title: 'Case Created',
    detail: formatDateTime(candidate.created_at),
    tone: 'neutral',
  });
  if (candidate.initiated_at) {
    entries.push({
      icon: <Clock className="h-4 w-4" />,
      title: 'Workflow Initiated',
      detail: formatDateTime(candidate.initiated_at),
      tone: 'neutral',
    });
  }
  entries.push({
    icon: <Clock className="h-4 w-4" />,
    title: `Current State — ${labelForState(state)}`,
    detail: formatDateTime(candidate.updated_at),
    tone: 'neutral',
  });
  if (candidate.completed_at) {
    entries.push({
      icon: <CheckCircle2 className="h-4 w-4" />,
      title: 'Onboarding Completed',
      detail: formatDateTime(candidate.completed_at),
      tone: 'success',
    });
  }
  if (state === 'rejected') {
    entries.push({
      icon: <AlertTriangle className="h-4 w-4" />,
      title: 'Onboarding Rejected',
      detail: candidate.rejection_reason || formatDateTime(candidate.updated_at),
      tone: 'error',
    });
  }

  const toneClass = (tone: TimelineEntry['tone']): string => {
    switch (tone) {
      case 'success':
        return 'text-green-600 bg-green-100';
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-rgf-gray-600 bg-rgf-gray-100';
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-base font-semibold text-rgf-gray-900">Timeline</h3>
      </div>
      <div className="card-body">
        <ol className="space-y-4">
          {entries.map((entry, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <span className={`mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full ${toneClass(entry.tone)}`}>
                {entry.icon}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-rgf-gray-900">{entry.title}</div>
                <div className="text-xs text-rgf-gray-500">{entry.detail}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};
